import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Play, Pause, TrendingUp, Clock, Users, Star } from 'lucide-react';
import { NaatKhawan, AudioFile } from '../types';
import { naatKhawanService, audioService } from '../services/api';
import { useAudioPlayer } from '../context/AudioPlayerContext';
import { useFavorites } from '../context/FavoritesContext';
import { useToast } from '../context/ToastContext';
import NaatCard from '../components/NaatCard';
import SEO from '../components/SEO';
import './HomePage.css';

const HomePage: React.FC = () => {
  const [popularAudios, setPopularAudios] = useState<AudioFile[]>([]);
  const [recentAudios, setRecentAudios] = useState<AudioFile[]>([]);
  const [featuredNaatKhawans, setFeaturedNaatKhawans] = useState<NaatKhawan[]>([]);
  const [loading, setLoading] = useState(true);
  const [isLoadingPlaylist, setIsLoadingPlaylist] = useState(false);
  const { state, playAudio, addToQueue, togglePlay, removeFromQueue } = useAudioPlayer();
  const { isFavorite, toggleFavorite } = useFavorites();
  const { showToast } = useToast();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [popularResponse, recentResponse, naatKhawansResponse] = await Promise.all([
          audioService.getPopular(6),
          audioService.getRecent(6),
          naatKhawanService.getByAudioCount(6)
        ]);

        if (popularResponse.success) {
          setPopularAudios(popularResponse.data);
        }

        if (recentResponse.success) {
          setRecentAudios(recentResponse.data);
        }

        if (naatKhawansResponse.success) {
          setFeaturedNaatKhawans(naatKhawansResponse.data);
        }
      } catch (error) {
        console.error('Error fetching home page data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handlePlayAudio = (audio: AudioFile) => {
    // If this audio is already playing, just toggle play/pause
    if (state.currentAudio?.id === audio.id) {
      togglePlay();
    } else {
      playAudio(audio);
    }
  };

  const handleAddToQueue = (audio: AudioFile) => {
    const wasAdded = addToQueue(audio);
    if (wasAdded) {
      showToast(`Added "${audio.title}" to queue`, 'success', 3000, 'bottom-right');
    } else {
      showToast(`"${audio.title}" is already in queue`, 'info', 3000, 'bottom-right');
    }
  };

  const handleToggleFavorite = (audio: AudioFile) => {
    const wasAlreadyFavorite = isFavorite(audio.id);
    toggleFavorite(audio);
    if (wasAlreadyFavorite) {
      showToast(`Removed "${audio.title}" from favorites`, 'info', 3000, 'top-right-favorites');
    } else {
      showToast(`Added "${audio.title}" to favorites`, 'success', 3000, 'top-right-favorites');
    }
  };

  const handleDownloadComplete = (audio: AudioFile, success: boolean) => {
    if (success) {
      showToast(`Downloaded "${audio.title}" successfully! 🎵`, 'success', 4000, 'top-right-favorites');
    } else {
      showToast(`Failed to download "${audio.title}". Please try again.`, 'error', 4000, 'top-right-favorites');
    }
  };

  const handleStartListening = async () => {
    if (isLoadingPlaylist) return; // Prevent multiple clicks
    
    setIsLoadingPlaylist(true);
    showToast('Starting Naats Radio...', 'info', 2000);
    
    try {
      // Fetch 20 random naats from the server
      const response = await audioService.getRandom(20);

      if (response.success && response.data && response.data.length > 0) {
        const randomNaats = response.data;

        // Clear existing queue first
        state.queue.forEach(() => removeFromQueue(0));

        // Play the random playlist - it will be added to queue automatically
        playAudio(randomNaats[0], randomNaats, 'regular');

        showToast(`Starting Naats Radio with ${randomNaats.length} naats`, 'success', 3000);
      } else {
        showToast('Unable to load naats. Please try again.', 'error');
      }
    } catch (error) {
      showToast('Error loading naats. Please try again.', 'error');
    } finally {
      setIsLoadingPlaylist(false);
    }
  };

  // Check if Start Listening button should show pause
  const isStartListeningPlaying = () => {
    return state.isPlaying && state.playlistSource !== 'favorites';
  };

  if (loading) {
    return (
      <div className="home-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading Homepage...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="home-page">
      <SEO
        title="Home - NaatCity | Islamic Audio Streaming Platform"
        description="Welcome to NaatCity - Your premier destination for Islamic Naats, Hamds, and Spiritual recitations. Stream thousands of spiritual recitations from renowned Naat Khawans including Junaid Jamshed, Owais Raza Qadri, and more. Free high-quality audio streaming."
        keywords="naatcity, islamic naats online, naat streaming, hamd, qawwali, naat khawan, islamic audio, urdu naats, arabic naats, junaid jamshed, owais raza qadri"
        url="https://naatcity.com"
      />

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <h1>Welcome to NaatCity</h1>
            <p>
              Experience the divine beauty of Islamic Naats through the melodious voices 
              of renowned Naat Khawans. Join our spiritual journey and immerse yourself 
              in the love of Prophet Muhammad (PBUH).
            </p>
            <div className="hero-buttons">
              <Link to="/naatkhawans" className="btn btn-primary">
                <Users size={18} />
                Naat Khawans
              </Link>
              <button
                className="btn btn-primary"
                onClick={() => {
                  if (isStartListeningPlaying()) {
                    togglePlay();
                  } else {
                    handleStartListening();
                  }
                }}
                disabled={isLoadingPlaylist}
                aria-label={isStartListeningPlaying() ? "Pause playback" : "Start listening to popular naats"}
              >
                {isLoadingPlaylist ? (
                  <>
                    <div className="btn-spinner"></div>
                    Loading...
                  </>
                ) : isStartListeningPlaying() ? (
                  <>
                    <Pause size={15} />
                    Pause
                  </>
                ) : (
                  <>
                    <Play size={15} />
                    Start Listening
                  </>
                )}
              </button>
            </div>
          </div>
          <div className="hero-image">
            <div className="hero-illustration">
              <div className="mosque-silhouette">
                <div className="minaret"></div>
                <div className="dome"></div>
                <div className="minaret"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Naats Section */}
      <section className="section popular-section">
        <div className="section-header">
          <Link to="/popular" className="section-title-link">
            <h2 className="section-title">
              <TrendingUp size={24} />
              Popular Naats
            </h2>
          </Link>
        </div>
        
        <div className="naat-card-grid">
          {popularAudios.map((audio, index) => {
            const isPlaying = state.currentAudio?.id === audio.id && state.isPlaying;
            return (
              <NaatCard
                key={audio.id}
                naat={audio}
                serialNumber={index + 1}
                isPlaying={isPlaying}
                isFavorite={isFavorite(audio.id)}
                onPlay={handlePlayAudio}
                onPause={() => togglePlay()}
                onAddToQueue={handleAddToQueue}
                onToggleFavorite={handleToggleFavorite}
                onDownloadComplete={handleDownloadComplete}
              />
            );
          })}
        </div>

        <div className="section-footer">
          <Link to="/popular" className="btn btn-outline">
            View All Popular Naats
          </Link>
        </div>
      </section>

      {/* Featured NaatKhawans Section */}
      <section className="section naatkhawans-section">
        <div className="section-header">
          <Link to="/naatkhawans" className="section-title-link">
            <h2 className="section-title">
              <Star size={24} />
              Naat Khawans
            </h2>
          </Link>
        </div>
        <div className="naatkhawan-grid">
          {featuredNaatKhawans.map((naatKhawan) => (
            <Link 
              key={naatKhawan.id} 
              to={`/naatkhawan/${naatKhawan.id}`}
              className="naatkhawan-card with-bottom-bar"
            >
              <div className="naatkhawan-info no-image">
                <h3 className="naatkhawan-name">{naatKhawan.name}</h3>
                <div className="naatkhawan-bottom">
                  <span className="naatkhawan-count">{naatKhawan.audioCount || 0} Naats</span>
                  {naatKhawan.urduName && (
                    <span className="naatkhawan-name-urdu inline">{naatKhawan.urduName}</span>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
        
        <div className="section-footer">
          <Link to="/naatkhawans" className="btn btn-outline">
            View All NaatKhawans
          </Link>
        </div>
      </section>

      {/* Recent Uploads Section */}
      <section className="section recent-section">
        <div className="section-header">
          <Link to="/recent" className="section-title-link">
            <h2 className="section-title">
              <Clock size={24} />
              Recent Additions
            </h2>
          </Link>
        </div>
        
        <div className="naat-card-grid recent-grid-mode">
          {recentAudios.map((audio, index) => {
            const isPlaying = state.currentAudio?.id === audio.id && state.isPlaying;
            return (
              <NaatCard
                key={audio.id}
                naat={audio}
                serialNumber={index + 1}
                isPlaying={isPlaying}
                isFavorite={isFavorite(audio.id)}
                onPlay={handlePlayAudio}
                onPause={() => togglePlay()}
                onAddToQueue={handleAddToQueue}
                onToggleFavorite={handleToggleFavorite}
                onDownloadComplete={handleDownloadComplete}
              />
            );
          })}
        </div>

        <div className="section-footer">
          <Link to="/recent" className="btn btn-outline">
            View All Recent Naats
          </Link>
        </div>
      </section>

    </div>
  );
};

export default HomePage;